import api, {
  CertificateRecordExportData,
  CertificateRecords,
  DeploymentStatusesJsonData,
  keyToString,
} from './MeshApi';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from './AuthService';
import { intlFormatDistance } from 'date-fns';

const TRUSTEE_PREFIX = 'com.hushmesh.agent-trustee-';
const AGENT_PREFIX = 'com.hushmesh.agent-';

function deployedActorNames(deployments: DeploymentStatusesJsonData[]): Set<string> {
  const result = new Set<string>();
  deployments.forEach(host => {
    host.statuses.forEach(status => {
      result.add(status.actor_name);
    });
  });
  return result;
}

function filterCertificates(
  records: CertificateRecords,
  deployments: DeploymentStatusesJsonData[],
): CertificateRecords {
  const deployed = deployedActorNames(deployments);

  const end_entities: Record<string, CertificateRecordExportData> = {};
  Object.entries(records.end_entities).forEach(([key, record]) => {
    if (record.display_name.startsWith(TRUSTEE_PREFIX)) {
      const foo = record.display_name.slice(TRUSTEE_PREFIX.length);
      if (!deployed.has(`${AGENT_PREFIX}${foo}`)) {
        return;
      }
    }
    end_entities[key] = record;
  });

  const referencedIntermediateKeys = new Set<string>();
  Object.values(end_entities).forEach(record => {
    referencedIntermediateKeys.add(
      keyToString({ display_name: record.parent_display_name, serial_number: record.parent_serial_number }),
    );
  });

  const intermediates: Record<string, CertificateRecordExportData> = {};
  Object.entries(records.intermediates).forEach(([key, record]) => {
    if (referencedIntermediateKeys.has(key)) {
      intermediates[key] = record;
    }
  });

  const guardians: Record<string, CertificateRecordExportData> = { ...records.guardians };

  const referencedRootKeys = new Set<string>();
  Object.values(intermediates).forEach(record => {
    referencedRootKeys.add(
      keyToString({ display_name: record.parent_display_name, serial_number: record.parent_serial_number }),
    );
  });
  Object.values(guardians).forEach(record => {
    referencedRootKeys.add(
      keyToString({ display_name: record.parent_display_name, serial_number: record.parent_serial_number }),
    );
  });

  const root: Record<string, CertificateRecordExportData> = {};
  Object.entries(records.root).forEach(([key, record]) => {
    if (referencedRootKeys.has(key)) {
      root[key] = record;
    }
  });

  return { guardians, end_entities, intermediates, root };
}

function displayCertificates(title: string, records: Record<string, CertificateRecordExportData>) {
  return (
    <div className="container">
      <h3 className="section-title">{title}</h3>
      <table className="certificates-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Serial</th>
            <th>Description</th>
            <th>Creation</th>
            <th>Expiration</th>
            <th>Parent</th>
            <th>Parent Serial</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(records)
            .sort((key1, key2) => {
              let record1 = records[key1];
              let record2 = records[key2];
              return record1.parent_display_name.localeCompare(record2.parent_display_name) || key1.localeCompare(key2);
            })
            .map(key => (
              <tr key={key}>
                <td>{records[key].display_name}</td>
                <td>{records[key].serial_number.substring(0, 8)}</td>
                <td>
                  {records[key].pubkey_algorithm_name} {records[key].pubkey_algorithm_additional_info} with{' '}
                  {records[key].signature_algorithm_hash}
                </td>
                <td>{intlFormatDistance(records[key].creation, new Date())}</td>
                <td>{intlFormatDistance(records[key].expiration, new Date())}</td>
                <td>{records[key].parent_display_name}</td>
                <td>{records[key].parent_serial_number.substring(0, 8)}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

const Certificates = () => {
  const [data, setData] = useState<CertificateRecords | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (data == null) {
          const [certificates, deployments] = await Promise.all([api.getCertificates(), api.getDeployments()]);
          console.log(certificates);
          console.log(deployments);
          setData(filterCertificates(certificates, deployments));
        }
      } catch (error) {
        console.log('Failed to fetch certificates:', error);
        authService.logout();
        navigate('/');
      }
    };

    fetchData();
  }, [data, navigate]);
  if (data == null) {
    return <div className="spinner"></div>;
  }
  return (
    <div>
      {displayCertificates('Guardians', data.guardians)}
      {displayCertificates('End-Entities', data.end_entities)}
      {displayCertificates('Intermediate', data.intermediates)}
      {displayCertificates('Root', data.root)}
    </div>
  );
};

export default Certificates;
